import * as React from 'react';
import { ActivitySpinnerWidget, ActivitySpinnerSize, TextLinkWidget } from '@tableau/widgets';
import { Messages } from '../../../codegen/Localize';
import { InterpolatedString } from '../../../common/react/components/InterpolatedString';
import { ShareActivity } from '../../../sharing/requests/ShareActivity';
import { L10n } from '../../../ts/L10n/L10n';
import { renderSharerProfilePicture } from '../CardView/SharedWithMeCard';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { MessageCharLimit } from '../Tooltips/SharedWithMeTooltip';
import { SharedViewsPopupStyle } from './style/SharedViewsPopupStyle';
import { ISharedVersionInfo } from '../../../ts/models/ISharedVersionInfo';

export const SharedViewsPopupId = 'shared-views-popup';
export const SharedViewsPopupTestId = 'shared-views-popup';
export const SharedViewsPopupDescriptionTestId = 'shared-views-popup-description';
export const SharedViewsPopupContentTestId = 'shared-views-popup-content';
export const SharedViewsPopupSpinnerTestId = 'shared-views-popup-loading';
export const SharedViewsPopupTitleTestId = 'shared-views-popup-title';
export const SharedViewDOMIdPrefix = 'shared-view-';
export const SharedCustomViewDOMIdPrefix = 'shared-custom-view-';
export const SharerNameTestId = 'sharer-name';
export const SharedTypeDescriptionTestId = 'shared-type-description';
export const SharedAtTestId = 'shared-at';

export type SharedViewsPopupProps = {
  /** Callback invoked after content loads */
  onLoadComplete: () => void;
  urls: SpacesSiteUrls;
  viewId: string;
};

export type SharedViewsPopupState = {
  sharedVersions?: ISharedVersionInfo[];
};

export class SharedViewsPopup extends React.Component<SharedViewsPopupProps, SharedViewsPopupState> {
  private sharedVersionsListRef: React.RefObject<HTMLDivElement> = React.createRef();
  public state: SharedViewsPopupState = {};

  public componentDidMount() {
    this.getSharedVersions();
  }

  public componentDidUpdate(prevProps: SharedViewsPopupProps, prevState: SharedViewsPopupState) {
    if (!prevState.sharedVersions && this.state.sharedVersions) {
      this.props.onLoadComplete();

      // Trigger focus on the first text link, use setTimeout so
      // this happens after popup gets re-positioned (TFSID 1116670)
      setTimeout(() => {
        const sharedVersionsList = this.sharedVersionsListRef.current;
        if (sharedVersionsList) {
          const textLinks = sharedVersionsList.getElementsByTagName('a');
          textLinks.length && textLinks[0].focus();
        }
      });
    }
  }

  private getSharedVersions = () => {
    ShareActivity.getShareActivity(this.props.viewId).then((sharedVersions: ISharedVersionInfo[]) => {
      this.setState({ sharedVersions, });
    });
  }

  private renderSpinner = (): JSX.Element => {
    return (
      <div className={SharedViewsPopupStyle.SpinnerContainer}>
        <ActivitySpinnerWidget
          spinnerSize={ActivitySpinnerSize.Small}
          testId={SharedViewsPopupSpinnerTestId}
        />
      </div>
    );
  }

  private renderShares = (): JSX.Element => {
    return (
      <div
        className={SharedViewsPopupStyle.ContentContainer}
        data-tb-test-id={SharedViewsPopupContentTestId}
      >
        <h1
          className={SharedViewsPopupStyle.Title}
          data-tb-test-id={SharedViewsPopupTitleTestId}
        >
          {Messages.sharedWithMeSharedVersionsPopupTitle({
            SHARED_VERSIONS_COUNT: this.state.sharedVersions.length.toString(),
          })}
        </h1>
        <p data-tb-test-id={SharedViewsPopupDescriptionTestId}>{Messages.sharedWithMeSharedVersionsDescription()}</p>
        {this.renderSharedVersionsList()}
      </div>
    );
  }

  private renderSharedVersionsList = (): JSX.Element => {
    return this.state.sharedVersions.length === 0 ? null : (
      <div
        className={SharedViewsPopupStyle.SharedViewsList}
        ref={this.sharedVersionsListRef}
      >
        { this.state.sharedVersions.map(this.renderSharedVersion) }
      </div>
    );
  }

  private renderSharedVersion = (data: ISharedVersionInfo): JSX.Element => {
    // check content type
    const contentType = data.contentType;
    if (contentType !== 'view' && contentType !== 'customView') {
      return null;
    }

    // build strings and elements
    const translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
    const sharer = data.sharer;
    const sharedAt = L10n.localizedDateTime(data.sharedAt);
    const sharerName = sharer.isDeleted ? Messages.sharedWithMeRemovedUser({ USERNAME: sharer.username }) : sharer.displayName;
    const renderSharerName: () => JSX.Element = () => (
      <span className={SharedViewsPopupStyle.SharerName} data-tb-test-id={SharerNameTestId}>
        {sharerName}
      </span>
    );

    let sharedViewDOMID: string;
    let sharedViewUrl: string;
    let sharedViewTypeDescriptionStringKey: string;
    let sharedViewLinkAriaLabel: string;
    if (contentType === 'view') {
      sharedViewDOMID = SharedViewDOMIdPrefix + data.id;
      sharedViewUrl = this.props.urls.redirectToViewUrl(data.id);
      sharedViewTypeDescriptionStringKey = 'sharedWithMeSharedVersionForView';
      sharedViewLinkAriaLabel = Messages.sharedWithMeSharedVersionOpenViewLabelForView({ NAME: sharerName, DATE_TIME: sharedAt });
    } else {
      sharedViewDOMID = SharedCustomViewDOMIdPrefix + data.id;
      sharedViewUrl = data.url;
      if (data.isHidden) {
        sharedViewTypeDescriptionStringKey = 'sharedWithMeSharedVersionForDirtyView';
        sharedViewLinkAriaLabel = Messages.sharedWithMeSharedVersionOpenViewLabelForDirtyView({ NAME: sharerName, DATE_TIME: sharedAt });
      } else {
        sharedViewTypeDescriptionStringKey = 'sharedWithMeSharedVersionForCustomView';
        sharedViewLinkAriaLabel = Messages.sharedWithMeSharedVersionOpenViewLabelForCustomView({ NAME: sharerName, DATE_TIME: sharedAt });
      }
    }

    // build UI
    return (
      <div
        key={sharedViewDOMID}
        id={sharedViewDOMID}
        className={SharedViewsPopupStyle.SharedViewContainer}
      >
        {renderSharerProfilePicture(sharer)}
        <div className={SharedViewsPopupStyle.SharedViewDetails}>
          <span data-tb-test-id={SharedTypeDescriptionTestId}>
            <InterpolatedString
              baseStringKey={sharedViewTypeDescriptionStringKey}
              translate={translate.instant}
              replacements={{
                USER_NAME: renderSharerName,
                ...(!data.isHidden) ? {
                  VIEW_NAME: () => <>{data.name}</>
                } : {}
              }}
            />
          </span>
          <div className={SharedViewsPopupStyle.SharedAt} data-tb-test-id={SharedAtTestId}>
            {sharedAt}
          </div>
          { data.message &&
            <div className={SharedViewsPopupStyle.Message}>
              {Messages.sharedWithMeMessage({ MESSAGE: L10n.elideIfLong(data.message, MessageCharLimit) })}
            </div>
          }
          <div className={SharedViewsPopupStyle.ViewLink}>
            <TextLinkWidget
              aria-label={sharedViewLinkAriaLabel}
              href={sharedViewUrl}
              target='_blank'
              testId=''
            >
              {Messages.sharedWithMeSharedVersionOpenView()}
            </TextLinkWidget>
          </div>
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    const content = this.state.sharedVersions ? this.renderShares() : this.renderSpinner();
    return (
      <div
        data-tb-test-id={SharedViewsPopupTestId}
        id={SharedViewsPopupId}
      >
        {content}
      </div>
    );
  }
}
